// Policy documents for eternitysingles®

export const TERMS_AND_CONDITIONS = `
TERMS AND CONDITIONS

Last Updated: August 22, 2026

Welcome to eternitysingles® ("the Platform", "we", "us", "our"). By accessing or using www.eternitysingles.com, you agree to be bound by these Terms and Conditions.

IMPORTANT NOTICE — BETA VERSION
eternitysingles® is currently in BETA MODE. This means the Platform is still under active development and testing. By using the Platform during the beta period, you acknowledge and accept the following:
- The Platform may contain bugs, errors, or incomplete features
- Service interruptions, downtime, or unexpected behaviour may occur without prior notice
- We cannot guarantee the security, integrity, or availability of your data during the beta period
- Data loss, corruption, or unauthorised access may occur despite our best efforts to prevent it
- Features may be added, modified, or removed at any time without notice
- Performance may vary and may not reflect the final product
- We strongly recommend not sharing highly sensitive personal information during the beta phase
- By using the beta version, you accept all associated risks and agree that eternitysingles®, Developer Tyrus, and affiliates shall not be held liable for any data leaks, data loss, security breaches, or service disruptions that may occur during the beta period
- We welcome your feedback to help improve the Platform — please report any issues to info@eternitysingles.com

1. ACCEPTANCE OF TERMS
By creating an account or using eternitysingles®, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, our Privacy Policy, and our Cookie Policy. If you do not agree, please do not use our Platform.

2. ELIGIBILITY
- You must be at least 18 years old to use this Platform
- You must provide accurate, current, and complete information during registration
- You must provide a valid mobile number, which is used to verify your identity during sign-up and login (Two-Step Verification)
- You are responsible for maintaining the confidentiality of your account credentials
- You may not create multiple accounts or use the account of another person
- You must comply with all applicable laws in your jurisdiction

2.1 ACCOUNT SECURITY & TWO-STEP VERIFICATION (2FA)
- To help protect your account, eternitysingles® uses Two-Step Verification (2FA) during sign-up and login
- Standard users: After entering your password, you must confirm your registered mobile number to complete sign-in
- Administrator account: In addition to mobile confirmation, the designated administrator account must enter a one-time verification code (OTP) sent to its email address
- One-time email verification codes are delivered through our email service provider (Resend) and expire after a short period (currently 10 minutes)
- Verification sessions are stored securely on our servers on a temporary basis and automatically expire; they are not used to track you
- You are responsible for keeping your registered email and mobile number accurate and for safeguarding access to them
- Passwords are stored only in encrypted (hashed) form using industry-standard bcrypt hashing and are never stored in plain text
- If you lose access to your mobile number or email, contact info@eternitysingles.com for account recovery assistance

3. USER CONDUCT
You agree to:
- Use the Platform for lawful purposes only
- Treat all users with respect, dignity, and kindness
- Not engage in harassment, bullying, stalking, or discriminatory behavior
- Not share inappropriate, offensive, or explicit content
- Not impersonate others or create fake/misleading profiles
- Not spam, solicit, or advertise commercial services
- Not attempt to access other users' accounts or data
- Not use the Platform for illegal activities
- Report any violations of these terms to our support team

4. MENTAL HEALTH SUPPORT DISCLAIMER
- eternitysingles® provides wellness resources and tools but is NOT a substitute for professional mental health care
- We are not licensed mental health professionals or therapists
- Our features (mood tracking, journaling, resources) are for personal wellness only
- If you or someone else is in immediate danger, call your local emergency number right away (000 in Australia, 911 in the US, 112 in the EU, 999 in the UK)

- AUSTRALIA — Crisis & Mental Health Support:
  • Emergency Services: 000
  • Lifeline Australia: 13 11 14 (24/7 crisis support) — text 0477 13 11 14 or chat at lifeline.org.au
  • Beyond Blue: 1300 22 4636 (24/7) — beyondblue.org.au
  • Suicide Call Back Service: 1300 659 467 (24/7)
  • 13YARN: 13 92 76 (24/7 support for Aboriginal & Torres Strait Islander peoples)
  • Kids Helpline: 1800 55 1800 (ages 5–25, 24/7)
  • MensLine Australia: 1300 78 99 78 (24/7)
  • 1800RESPECT: 1800 737 732 (sexual assault, domestic & family violence)

- UNITED STATES — Crisis Support:
  • Emergency Services: 911
  • 988 Suicide & Crisis Lifeline: call or text 988
  • Crisis Text Line: text HOME to 741741

- WORLDWIDE — Crisis Support:
  • Find a Helpline (global directory by country): findahelpline.com
  • International Association for Suicide Prevention (crisis centres): iasp.info/resources/Crisis_Centres
  • Befrienders Worldwide: befrienders.org
  • United Kingdom & ROI — Samaritans: 116 123 (24/7)
  • Canada — Talk Suicide Canada: 1-833-456-4566 (or text 45645)
  • New Zealand — 1737: call or text 1737 (Need to Talk)
  • Otherwise, contact your local emergency number or nearest crisis helpline

- We strongly encourage seeking professional help from licensed therapists
- Content shared by other users does not constitute professional advice

5. AI WELLNESS COUNSELOR (ETERNITY)
- Our AI Wellness Counsellor "Eternity" is powered by artificial intelligence technology (OpenAI GPT-5.2)
- Eternity provides supportive conversations for mental wellness and dating advice
- Eternity is NOT a licensed therapist, psychologist, or medical professional
- Conversations with Eternity are NOT a substitute for professional mental health care
- AI responses are generated based on patterns and should not be taken as medical advice
- Conversations with Eternity are ephemeral and not permanently stored
- You may clear your conversation history at any time
- If you are experiencing a mental health crisis, please seek professional help immediately
- We do not guarantee the accuracy or appropriateness of AI-generated responses
- You use the AI Counsellor feature at your own discretion

6. DATE PLANNING ASSISTANT
- Our AI Date Planning Assistant suggests personalized date ideas based on shared interests and profiles
- Suggestions are AI-generated (using OpenAI GPT-5.2) and for inspiration only
- The assistant considers both users' interests, hobbies, and preferences to suggest compatible activities
- We do not guarantee the quality, safety, or availability of suggested activities
- Always exercise personal judgment when planning dates
- We are not responsible for outcomes of dates planned using this feature
- Suggestions may include venues, activities, or experiences that require separate booking or fees

7. PRIVACY AND DATA PROTECTION
- Your personal information is handled according to our Privacy Policy
- You consent to the collection, processing, and use of your data as described
- Your mental health data (mood tracking, journals) remains private to you
- We will never share your wellness data without explicit consent
- You may request data export or account deletion at any time through Settings
- We comply with applicable data protection laws (GDPR, CCPA)

8. CONTENT AND INTELLECTUAL PROPERTY
- You retain ownership of all content you post (photos, messages, profile information)
- By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use, display, reproduce, and distribute your content on the Platform
- You must not post content that:
  • Violates copyright, trademark, or intellectual property rights
  • Contains false, misleading, or defamatory information
  • Violates another person's privacy or dignity
  • Promotes illegal activities or violence
- We reserve the right to remove content that violates these terms
- The eternitysingles® name, logo, and Platform design are our intellectual property. "eternitysingles®" is a registered trademark, and its use requires our prior written permission
- All rights not expressly granted are reserved by eternitysingles® and Developer Tyrus

8.1 PHOTOS AND MEDIA
- You may upload one or more photos to your profile and choose one as your profile picture
- Uploaded photos are stored on secure third-party cloud object storage, not solely on the device you upload from
- The photo you set as your profile picture is visible to other members across the Platform (for example, in matches, messaging/conversation lists, discovery, and swipe cards)
- Automated content moderation may screen uploaded images; we may remove any image that violates these Terms
- You must only upload images that you own or have permission to use, and photos should genuinely represent you
- Deleted photos are first moved to a recycle bin and can be restored within 30 days, after which they are permanently deleted
- You must not upload nudity, sexually explicit, illegal, hateful, or infringing imagery, or photos of other people without their consent

9. WELLNESS FEATURES
- Mood tracking, journaling, and wellness tools are for personal development only
- Data from these features is private and only visible to you unless you choose to share
- Wellness resources are educational and do not replace professional care
- We do not guarantee specific mental health outcomes from using these features
- You are responsible for how you use wellness information and tools

10. MATCHING AND CONNECTIONS
- Our matching algorithm considers mental health compatibility, wellness goals, interests, and preferences
- Compatibility scores are estimates based on profile information, not guarantees
- We do not guarantee specific outcomes, matches, or relationships
- Users are solely responsible for their interactions and decisions
- Always prioritize your safety when meeting others in person:
  • Meet in public places for first dates
  • Tell a friend or family member your plans
  • Trust your instincts
  • Never share financial information
- Report suspicious behavior immediately

11. AUTO-FRIEND WELCOME FEATURE
- Upon registration, new users are automatically connected with the official eternitysingles® account
- This creates an automatic match allowing immediate communication
- The purpose is to provide welcome support and assistance to new users
- Users can unmatch from this connection at any time
- This feature helps new users get started with the Platform

12. ADMIN BADGE AND VERIFICATION
- Official administrators are identified by an "Admin" badge on their profiles
- Admin badges indicate staff members who help manage the Platform
- The admin badge is visible when viewing admin profiles
- Only authorized personnel can be designated as administrators
- If you encounter someone falsely claiming to be an admin, report them immediately

13. PROHIBITED ACTIVITIES
You may not:
- Use the Platform for commercial purposes without permission
- Scrape, copy, or extract user data
- Use automated systems (bots) to access the Platform
- Reverse engineer or attempt to access our source code
- Interfere with Platform security or functionality
- Create accounts for fraudulent purposes
- Solicit personal information from minors
- Engage in financial scams or fraud
- Misuse or abuse the AI Counsellor feature
- Impersonate administrators or staff members

14. FREE TRIAL AND PREMIUM SUBSCRIPTION

14.1 FREE TRIAL
- All new users receive a complimentary 7-DAY FREE TRIAL of Premium features upon registration
- The free trial begins immediately upon account creation
- No credit card or payment information is required to start the free trial
- You will receive a notification before your free trial expires
- After the 7-day trial period, you will automatically revert to the Free tier unless you subscribe

14.2 PREMIUM FEATURES INCLUDED IN FREE TRIAL
During your 7-day free trial, you will have access to all Premium features including:

MATCHING & DISCOVERY:
- Unlimited daily swipes (Free tier: 10 swipes/day)
- See who liked your profile
- Advanced search filters (age, location, interests, mental health goals)
- 5 Super Likes per week to stand out
- Profile boost for increased visibility

MESSAGING & COMMUNICATION:
- Unlimited messaging with matches
- Read receipts to see when messages are read
- Priority message delivery
- Send intro messages with your likes

AI-POWERED FEATURES:
- Unlimited access to AI Wellness Counsellor "Eternity"
- AI Date Planning Assistant for personalised date ideas
- AI-powered compatibility insights

PRIVACY & CONTROL:
- Incognito mode - browse profiles without being seen
- Control who sees your online status
- Hide your profile from specific users

EXCLUSIVE BENEFITS:
- Priority customer support
- Early access to new features
- Ad-free experience
- Exclusive community events access

14.3 SUBSCRIPTION PRICING (After Free Trial)
- Premium: $14.99/month - Includes all matching, messaging, and discovery features
- Premium Plus: $29.99/month - Includes all Premium features PLUS unlimited AI Counsellor and Date Planning
- Annual plans available at discounted rates

14.4 PAYMENT TERMS
- Subscription fees will be clearly disclosed before purchase
- Payments are processed securely through our payment provider
- Subscriptions auto-renew unless cancelled before the renewal date
- You may cancel your subscription at any time through Settings → Account → Subscription
- Refund requests are handled on a case-by-case basis within 14 days of purchase

14.5 FREE TIER (AFTER TRIAL)
When your 7-day free trial ends, you automatically move to the Free tier unless you subscribe. The Free tier remains genuinely useful and always free, and includes:

- Profile creation, editing, and photo uploads (with your chosen profile picture)
- Up to 10 swipes per day in Discover/Swipe
- Mutual matching and messaging with people you have matched with
- Auto-connection with the official eternitysingles® account for welcome support
- Mood tracking and private journaling
- Wellness resources and self-care tools
- Support groups and community group chats
- Basic profile browsing and viewing matches
- Two-Step Verification (2FA) account security

The following remain Premium-only after the trial (see 14.2 for the full list):
- Unlimited daily swipes and profile boost
- Seeing who liked your profile
- Advanced search filters and weekly Super Likes
- Read receipts and priority message delivery
- Unlimited AI Wellness Counsellor ("Eternity") and AI Date Planning Assistant
- Incognito mode and online-status controls
- Ad-free experience, priority support, and early access to new features

You can upgrade at any time through Settings → Account → Subscription, and you keep all Free-tier features whether or not you subscribe.

15. TERMINATION
We reserve the right to:
- Suspend or terminate accounts that violate these Terms
- Remove content that breaches our policies
- Refuse service to anyone for any lawful reason
- Modify or discontinue the Platform with notice

You may terminate your account at any time through Settings → Account → Delete Account.

16. BETA MODE DISCLAIMER AND LIMITATION OF LIABILITY

16.1 BETA STATUS
- eternitysingles® is currently operating in BETA MODE
- The Platform is provided on an "as is" and "as available" basis during the beta period
- We make no warranties, express or implied, regarding the reliability, accuracy, or completeness of the Platform

16.2 DATA AND SECURITY RISKS
- During the beta period, we cannot guarantee protection against data leaks, breaches, or unauthorised access
- Data loss or corruption may occur due to bugs, system failures, or unforeseen technical issues
- Users are advised to exercise caution when sharing personal or sensitive information
- We implement reasonable security measures but cannot guarantee absolute data protection during beta

16.3 LIMITATION OF LIABILITY
- To the maximum extent permitted by law, eternitysingles®, Developer Tyrus, and all affiliates shall not be liable for any direct, indirect, incidental, special, or consequential damages arising from:
  • Data leaks, breaches, or unauthorised access to user data
  • Loss or corruption of user data, profiles, messages, or other content
  • Service interruptions, downtime, or system failures
  • Bugs, errors, or unexpected Platform behaviour
  • Any actions taken based on AI-generated content or advice
- Your use of the Platform during the beta period is entirely at your own risk
- This limitation applies regardless of the theory of liability (contract, tort, negligence, or otherwise)

16.4 INDEMNIFICATION DURING BETA
- You agree to hold harmless and indemnify eternitysingles® against any claims related to data loss, security incidents, or service disruptions during the beta period

17. DISCLAIMERS AND LIMITATIONS
- The Platform is provided "AS IS" without warranties of any kind
- We do not guarantee uninterrupted, error-free, or secure service
- As a beta product, additional risks apply (see Section 16)
- We are not liable for:
  • User-generated content or behavior
  • Technical failures or data loss
  • Outcomes of relationships formed through the Platform
  • Third-party links or resources
  • AI-generated advice or suggestions
- Our liability is limited to the maximum extent permitted by law

18. INDEMNIFICATION
You agree to indemnify and hold harmless eternitysingles®, Developer Tyrus, and our affiliates from claims arising from:
- Your use of the Platform
- Your violation of these Terms
- Your violation of others' rights
- Content you post
- Your use of AI features

19. DISPUTE RESOLUTION
- These Terms are governed by applicable law in your jurisdiction
- Disputes will be resolved through good faith negotiation first
- If unresolved, disputes may be subject to arbitration or court proceedings

20. CHANGES TO TERMS
- We may update these Terms at any time
- Material changes will be notified via email or Platform notification
- Continued use after changes constitutes acceptance
- Last updated date will always be displayed at the top

21. CONTACT
For questions, concerns, or reports regarding these Terms:
- Email: info@eternitysingles.com
- Support: Available through Settings → Help & Support
- Response time: 24-48 hours

22. ACKNOWLEDGMENT
By using eternitysingles®, you acknowledge that:
- You have read and understood these Terms
- You are legally able to enter into this agreement
- You will use the Platform responsibly and respectfully
- You understand this is a dating platform with wellness features, not therapy
- You understand the AI Counsellor is not a replacement for professional care
- You understand auto-friend and admin badge features
- You understand and consent to Two-Step Verification (2FA) using your mobile number (and, for the administrator account, an email one-time code)
- You understand the Platform is in BETA MODE and accept the associated risks
- You acknowledge that data leaks, loss, or security breaches may occur during the beta period and agree to hold eternitysingles® harmless

---

eternitysingles®
Connecting hearts, nurturing minds.
© 2026 All rights reserved. Developer Tyrus.
Terms and Conditions Version 1.7
`;

export const PRIVACY_POLICY = `
PRIVACY POLICY

Last Updated: August 22, 2026

Welcome to eternitysingles®. This Privacy Policy explains how we collect, use, protect, and share your information when you use our Platform at www.eternitysingles.com.

IMPORTANT NOTICE — BETA VERSION
eternitysingles® is currently in BETA MODE. During this period:
- While we implement reasonable security measures, we cannot guarantee complete protection of your personal data
- Data breaches, leaks, or unauthorised access may occur despite our best efforts
- We strongly advise users to exercise caution when sharing sensitive personal information
- We recommend against sharing financial details, government IDs, or other highly sensitive data on the Platform during the beta period
- By continuing to use the Platform, you accept these risks and acknowledge that eternitysingles® shall not be held liable for any data security incidents during the beta phase

1. INTRODUCTION
Developer Tyrus ("we", "our", "us") operates eternitysingles® and is committed to protecting your privacy. This policy applies to all users of our Platform. By using eternitysingles®, you consent to the data practices described in this policy.

2. INFORMATION WE COLLECT

2.1 INFORMATION YOU PROVIDE
- Account Information: Email, username, password (encrypted), full name, date of birth
- Mobile Number: Collected at registration and used for Two-Step Verification (2FA) at sign-up and login, and for account recovery
- Profile Information: Age, location, bio, interests, hobbies
- Photos: Profile photos you upload, including the one you set as your profile picture (stored on secure cloud object storage)
- Mental Health Information: Mental health goals, therapy experience, openness level (voluntary)
- Preferences: Relationship goals, looking for, body type preferences, children preferences, lifestyle information
- Communications: Messages, group chats, support requests
- Wellness Data: Mood entries, journal entries, wellness activities (private to you)
- AI Counsellor Conversations: Temporary session data (not permanently stored)

2.2 AUTOMATICALLY COLLECTED INFORMATION
- Usage Data: Pages viewed, features used, time spent, click patterns
- Device Information: Browser type, operating system, device type, IP address
- Location Data: Approximate location based on IP (not GPS tracking)
- Cookies and Similar Technologies: See our Cookie Policy

2.3 INFORMATION FROM THIRD PARTIES
- None. We do not collect data from third-party sources or data brokers

3. HOW WE USE YOUR INFORMATION

3.1 PRIMARY PURPOSES
- Provide Platform Services: Create your profile, enable matching, facilitate messaging
- Matching Algorithm: Calculate compatibility based on mental health goals, interests, preferences
- Personalization: Customize your experience and show relevant content
- AI Features: Power the AI Wellness Counsellor (Eternity) and Date Planning Assistant
- Communication: Send notifications, updates, and respond to inquiries
- Safety and Security: Detect fraud, prevent abuse, enforce Terms of Service
- Improvement: Analyze usage to improve features and user experience

3.2 AI FEATURES
- The AI Wellness Counsellor (Eternity) uses your profile information (name, interests, goals) to personalize responses
- Your conversation history with Eternity is session-based and not permanently stored
- The Date Planning Assistant uses both users' profile information to generate compatible date suggestions
- AI-generated content is processed in real-time using OpenAI GPT-5.2 and not retained after your session
- AI features are designed to support your wellness journey, not replace professional care

3.3 WELLNESS FEATURES
- Your mood tracking and journal entries are PRIVATE and stored securely
- We use aggregated, anonymized wellness data to improve features
- Individual wellness data is NEVER shared with other users or third parties

3.4 AUTO-FRIEND FEATURE
- When you register, an automatic connection is created with the eternitysingles® official account
- This allows us to welcome and support new users
- This connection data (match record) is stored like any other match
- You can remove this connection at any time

3.5 ADMIN FEATURES
- Administrator accounts have access to user management features
- Admin actions (profile edits, account management) are logged for security
- Admins can view user profiles, statistics, and perform moderation
- Admin access requires password re-verification each time the Admin Dashboard is opened (no persistent bypass is stored)
- Admin data access is limited to authorized personnel only

3.6 TWO-STEP VERIFICATION (2FA)
- We use your mobile number to verify your identity at sign-up and login
- For the designated administrator account, we also send a one-time verification code (OTP) to its email address via our email provider (Resend)
- Verification codes and sessions are stored temporarily on our servers and automatically expire (currently after 10 minutes); they are deleted once verification completes
- We use this data solely to secure account access and prevent unauthorised logins — not for marketing or tracking

3.7 LEGAL OBLIGATIONS
- Comply with laws, regulations, and legal processes
- Protect rights, property, and safety of users and the Platform
- Enforce our Terms and Conditions

4. HOW WE SHARE YOUR INFORMATION

4.1 INFORMATION VISIBLE TO OTHER USERS
- Profile Information: First name, age, bio, interests, relationship goals
- Profile Photos: The photo you set as your profile picture, and any photos on your profile, are visible to other members across matches, messaging, discovery, and swipe cards
- Mental Health Goals: Only if you choose to display them on your profile
- Compatibility Scores: Shown to mutual matches
- Messages: Only visible to conversation participants
- Admin Badge: Visible on profiles of official administrators

4.2 INFORMATION WE DO NOT SHARE
- Your email address (unless you choose to share it)
- Your password (always encrypted)
- Your mood tracking data
- Your journal entries
- Your therapy experience details (shown as categories only)
- Precise location (only city/region shown)
- Your conversations with the AI Counsellor
- Your full name (only first name shown to other users)
- Your date of birth (only age shown)

4.3 SERVICE PROVIDERS
We may share information with trusted third-party service providers who:
- Host our Platform (cloud infrastructure)
- Provide database services (MongoDB Atlas)
- Provide secure cloud object storage for user-uploaded photos
- Provide AI services (OpenAI for Eternity AI Counsellor and Date Planning Assistant)
- Deliver transactional and verification emails, such as Two-Step Verification codes (Resend)
- Assist with analytics (anonymized data only)
- Help with customer support
All providers are contractually obligated to protect your data and use it only for specified purposes.

4.4 LEGAL REQUIREMENTS
We may disclose information if required by:
- Law enforcement or government agencies
- Court orders or subpoenas
- Legal processes and investigations
- Emergency situations involving safety risks

4.5 BUSINESS TRANSFERS
If eternitysingles® is acquired or merged, your information may be transferred. You will be notified of any such change.

4.6 WITH YOUR CONSENT
We will only share your information for purposes not described here with your explicit consent.

5. DATA RETENTION

- Active Accounts: We retain your data while your account is active
- Deleted Accounts: Data is permanently deleted within 30 days of account deletion
- Photos: Deleted photos are moved to a recycle bin and can be restored within 30 days, after which they are permanently deleted from cloud storage
- AI Conversations: Not permanently stored; cleared after each session
- Legal Requirements: Some data may be retained longer if required by law
- Backups: Data may exist in backups for up to 90 days after deletion
- Anonymized Data: Aggregated, non-identifiable data may be retained indefinitely for analytics
- Admin Logs: Administrative action logs retained for security auditing

6. YOUR PRIVACY RIGHTS

6.1 ACCESS AND CONTROL
- View Profile: See all information in your profile through Settings
- Edit Information: Update profile, preferences, and wellness settings anytime
- Export Data: Download your data in JSON format through Settings
- Delete Account: Permanently delete your account and all associated data
- Clear AI History: Clear conversation history with Eternity at any time

6.2 COMMUNICATION PREFERENCES
- Opt out of marketing emails (service emails still sent)
- Control notification settings
- Manage visibility settings

6.3 REGIONAL RIGHTS (GDPR, CCPA)
Depending on your location, you may have additional rights:
- Right to access your personal data
- Right to rectification (correction)
- Right to erasure ("right to be forgotten")
- Right to data portability
- Right to object to processing
- Right to withdraw consent
- Right to lodge a complaint with supervisory authorities

To exercise these rights, contact: info@eternitysingles.com

7. DATA SECURITY

7.1 SECURITY MEASURES
- Encryption: Passwords encrypted with bcrypt, data transmitted via HTTPS
- Authentication: JWT token-based secure authentication
- Two-Step Verification (2FA): Mobile-number verification for all users at sign-up/login, plus an email one-time code (OTP) for the administrator account
- Admin Verification: Password re-verification required each time the Admin Dashboard is opened
- Access Controls: Limited employee access to personal data
- Regular Security Audits: Ongoing monitoring and testing
- Secure Infrastructure: Kubernetes deployment with security best practices
- AI Data Protection: AI conversations are not permanently stored
- Photo Storage: Uploaded photos are stored on secure cloud object storage with restricted, access-controlled retrieval
- Session Management: Verification and admin sessions are stored server-side, are short-lived, and expire automatically

7.2 YOUR RESPONSIBILITY
- Use strong, unique passwords
- Keep login credentials confidential
- Log out from shared devices
- Report suspicious activity immediately

7.3 NO GUARANTEE
While we implement strong security measures, no system is 100% secure. As the Platform is currently in BETA MODE, additional security risks may exist. We cannot guarantee absolute security and shall not be held liable for any data leaks, breaches, or unauthorised access that may occur during the beta period. We commit to industry-standard protection and continuous improvement of our security measures.

8. CHILDREN'S PRIVACY

- eternitysingles® is for users 18 years and older ONLY
- We do not knowingly collect information from minors
- If we discover a user is under 18, we will immediately delete their account and data
- Parents/guardians: Contact us if you believe a minor has created an account

9. INTERNATIONAL DATA TRANSFERS

- Our servers are located in the United States
- By using the Platform, you consent to data transfer to the US
- We ensure appropriate safeguards for international transfers
- Data protection standards are maintained regardless of location

10. THIRD-PARTY LINKS

- Our Platform may contain links to external websites
- We are not responsible for privacy practices of third-party sites
- Please review privacy policies of any external sites you visit

11. COOKIES AND TRACKING

- See our Cookie Policy for detailed information
- We use cookies for authentication, preferences, and analytics
- Admin dashboard access requires password re-verification each time it is opened (no persistent verification is stored)
- You can control cookie settings in your browser
- Disabling cookies may affect Platform functionality

12. CHANGES TO PRIVACY POLICY

- We may update this Privacy Policy periodically
- Material changes will be notified via email or Platform notification
- "Last Updated" date will always be current
- Continued use after changes indicates acceptance
- You can review policy history by contacting us

13. CALIFORNIA PRIVACY RIGHTS (CCPA)

California residents have specific rights under CCPA:
- Right to know what personal information is collected
- Right to know if personal information is sold or shared (we do NOT sell data)
- Right to access personal information
- Right to delete personal information
- Right to opt-out of sale (not applicable - we don't sell data)
- Right to non-discrimination for exercising CCPA rights

To exercise CCPA rights: info@eternitysingles.com

14. EUROPEAN PRIVACY RIGHTS (GDPR)

EU/EEA residents have rights under GDPR:
- Legal basis for processing: Consent, contract performance, legitimate interests
- Right of access, rectification, erasure, and portability
- Right to object and restrict processing
- Right to withdraw consent
- Right to lodge complaints with supervisory authorities
- Data Protection Officer contact: info@eternitysingles.com

15. AI AND AUTOMATED DECISION-MAKING

- Our matching algorithm uses automated processing to suggest compatible profiles
- The AI Counsellor (Eternity) uses AI (OpenAI GPT-5.2) to generate supportive responses
- The Date Planning Assistant uses AI to suggest personalized date ideas
- You have the right to not be subject to decisions based solely on automated processing
- Contact us if you wish to contest automated decisions

16. DO NOT TRACK SIGNALS

- Our Platform does not currently respond to Do Not Track (DNT) browser signals
- We may implement DNT compliance in the future

17. CONTACT US

For privacy questions, concerns, or requests:
- Email: info@eternitysingles.com
- Subject Line: "Privacy Inquiry"
- Support Portal: Settings → Help & Support → Contact Support
- Response Time: 48-72 hours for privacy requests

For data subject requests (access, deletion, etc.):
- Clearly state your request and provide account verification
- We will respond within 30 days (45 days for complex requests)

18. ACKNOWLEDGMENT

By using eternitysingles®, you acknowledge:
- You have read and understood this Privacy Policy
- You consent to collection and use of information as described
- You understand your rights and how to exercise them
- You know how to contact us with privacy concerns
- You understand how AI features use your data
- You understand admin and auto-friend features
- You understand the Platform is in BETA MODE and data security cannot be fully guaranteed
- You accept the risks associated with sharing personal data during the beta period

---

eternitysingles®
Connecting hearts, nurturing minds.
© 2026 All rights reserved. Developer Tyrus.
Privacy Policy Version 1.5
`;

export const COOKIES_POLICY = `
COOKIE POLICY

Last Updated: August 22, 2026

This Cookie Policy explains how eternitysingles® uses cookies and similar tracking technologies on www.eternitysingles.com.

IMPORTANT NOTICE — BETA VERSION
eternitysingles® is currently in BETA MODE. During the beta period, cookie and tracking implementations may change without notice. While we take reasonable steps to protect data stored in cookies and local storage, we cannot guarantee complete security against data leaks or unauthorised access during the beta phase.

1. WHAT ARE COOKIES?

Cookies are small text files stored on your device (computer, smartphone, tablet) when you visit a website. They help websites:
- Remember your preferences and settings
- Keep you logged in
- Understand how you use the site
- Improve your experience

2. TYPES OF COOKIES WE USE

2.1 ESSENTIAL COOKIES (Always Active)
These cookies are necessary for the Platform to function and cannot be disabled:

- Authentication Cookies: Keep you logged in between sessions
- Security Cookies: Protect against fraud and unauthorized access
- Two-Step Verification: During sign-up/login, identity verification (mobile number, and an email one-time code for the administrator account) is handled via short-lived sessions stored on our servers — not in cookies
- Session Cookies: Remember your actions during a browsing session
- Load Balancing: Ensure proper Platform performance
- AI Session Cookies: Maintain context during AI Counsellor conversations

Without these cookies, core features like login, messaging, AI Counsellor, admin access, and profile viewing will not work.

2.2 PREFERENCE COOKIES (Optional)
These cookies remember your choices and settings:

- Language preferences
- Display settings (light/dark mode)
- Notification preferences
- Layout customizations
- AI Counsellor preferences
- Admin dashboard theme preference

Disabling these cookies means you'll need to reset preferences each visit.

2.3 ANALYTICS COOKIES (Optional)
These cookies help us understand Platform usage:

- Pages visited and time spent
- Features used most frequently
- User flow and navigation patterns
- AI feature usage statistics (anonymized)
- Admin dashboard usage (anonymized)
- Performance metrics

Data collected is aggregated and anonymized. We use this to improve the Platform.

2.4 ADVERTISING COOKIES (Not Currently Used)
We do NOT currently use advertising or tracking cookies for marketing purposes. If this changes, we will update this policy and notify you.

3. COOKIES WE USE

| Cookie Name | Purpose | Type | Duration |
|-------------|---------|------|----------|
| auth_token | Authentication | Essential | 30 days |
| session_id | Session management | Essential | Session |
| user_prefs | User preferences | Preference | 1 year |
| theme_mode | Light/dark mode | Preference | 1 year |
| analytics_id | Usage analytics | Analytics | 2 years |
| ai_session | AI Counsellor context | Essential | Session |

Note: Two-Step Verification and Admin Dashboard access are NOT stored in cookies or browser storage. Identity verification uses short-lived sessions on our servers, and admin access requires re-entering your password each time the dashboard is opened.

4. SESSION STORAGE

In addition to cookies, we use browser sessionStorage for:

- Temporary UI State: Maintains modal and form states
- AI Conversation Context: Temporary context for AI Counsellor sessions

SessionStorage data is automatically cleared when you close your browser. Note: Admin Dashboard verification is no longer persisted in browser storage — your password is required each time you open the Admin Dashboard.

5. THIRD-PARTY COOKIES

We may use third-party services that set their own cookies:

- Cloud Infrastructure: For hosting and performance
- Analytics Services: For usage statistics (anonymized)
- AI Services: For powering the AI Counsellor (Eternity) and Date Planning Assistant

These services are carefully selected and have their own privacy policies. We do NOT use:
- Social media tracking cookies
- Advertising network cookies
- Cross-site tracking cookies

6. HOW TO MANAGE COOKIES

6.1 BROWSER SETTINGS
You can control cookies through your browser settings:

- Chrome: Settings → Privacy and Security → Cookies
- Firefox: Settings → Privacy & Security → Cookies
- Safari: Preferences → Privacy → Cookies
- Edge: Settings → Privacy → Cookies

Options include:
- Block all cookies
- Block third-party cookies only
- Delete cookies after each session
- View and delete individual cookies

6.2 OPT-OUT
You can opt out of optional cookies:
- Through your Settings page on eternitysingles®
- By managing browser cookie preferences
- By using browser privacy/incognito mode

6.3 IMPACT OF DISABLING COOKIES
Disabling cookies may affect Platform functionality:
- You'll be logged out frequently
- Preferences won't be saved
- Some features may not work properly
- We can't remember your settings
- AI Counsellor context won't be maintained between messages
- Admin dashboard will require verification each session

Essential cookies cannot be disabled if you want to use the Platform.

7. OTHER TRACKING TECHNOLOGIES

7.1 LOCAL STORAGE
We use browser local storage for:
- Caching data for faster load times
- Caching profile images and avatars for faster loading
- Storing non-sensitive preferences
- Improving offline functionality
- Temporary AI conversation context
- Authentication tokens

7.2 SESSION STORAGE
We use browser session storage for:
- Temporary form data
- UI state management

Note: Admin Dashboard access is verified by re-entering your password each time and is not stored in session storage.

7.3 WEB BEACONS / PIXELS
We currently do NOT use web beacons or tracking pixels. If implemented, we will update this policy.

7.4 MOBILE APP (Future)
If we develop a mobile app, additional tracking technologies may be used. A separate policy will be provided.

8. DO NOT TRACK (DNT)

Our Platform does not currently respond to Do Not Track browser signals. This is because:
- No industry standard exists for DNT implementation
- Essential cookies are necessary for functionality
- You can manage cookies through browser settings

We may implement DNT compliance as standards evolve.

9. COOKIE CONSENT

9.1 HOW WE OBTAIN CONSENT
- First-time visitors see a cookie notice
- You can accept or manage cookie preferences
- Essential cookies are automatically active
- Optional cookies require consent

9.2 WITHDRAWING CONSENT
You can withdraw cookie consent anytime:
- Change settings in your Profile → Privacy Settings
- Clear cookies through browser settings
- Use incognito/private browsing mode

10. UPDATES TO COOKIE POLICY

- We may update this Cookie Policy as we add features
- Material changes will be notified via Platform notification
- "Last Updated" date reflects the latest version
- Continued use indicates acceptance of changes

11. CONTACT US

For questions about cookies or this policy:
- Email: info@eternitysingles.com
- Subject: "Cookie Policy Inquiry"
- Support: Settings → Help & Support

12. CONSENT

By using eternitysingles®, you consent to our use of essential cookies as described. You can manage optional cookies through your settings.

13. ACKNOWLEDGMENT

By continuing to use our Platform:
- You acknowledge reading this Cookie Policy
- You understand what cookies we use and why
- You know how to manage your cookie preferences
- You consent to cookies as per your settings
- You understand session storage for admin verification
- You understand the Platform is in BETA MODE and cookie/tracking implementations may change

---

eternitysingles®
Connecting hearts, nurturing minds.
© 2026 All rights reserved. Developer Tyrus.
Cookie Policy Version 1.5
`;

export const downloadPolicy = (policyName, content) => {
  try {
    console.log('Starting download for:', policyName);
    
    const filename = `eternitysingles-${policyName.toLowerCase().replace(/\s+/g, '-')}.txt`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    
    // Create download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    
    console.log('Download completed for:', filename);
    return true;
  } catch (error) {
    console.error('Error downloading policy:', error);
    return false;
  }
};

export const copyToClipboard = async (content, policyName) => {
  try {
    await navigator.clipboard.writeText(content);
    console.log(`${policyName} copied to clipboard`);
    return { success: true, message: `${policyName} copied to clipboard` };
  } catch (error) {
    console.error('Failed to copy:', error);
    return { success: false, message: 'Failed to copy to clipboard' };
  }
};
